// Facets are the page-level context beside the scope (ZENITH.md §18, #254):
// the device, network, station, SSID or audit target a page is narrowed to.
// Like the scope they live in the address — `?serial=…`, `?network=…`,
// `?mac=…`, `?ssid=…`, `?target_type=…&target_id=…` — so a link, the back
// button and a second tab carry them; unlike the scope they are not
// remembered, because a device is a thing to look at and not a place to be.
// They combine (#263): "this station, on this access point, on this SSID"
// is one address, and each facet is cleared on its own.
//
// A facet belongs to the pages that understand it and to no other. A link
// inside a page carries one only to a page that does (Devices ↔ Clients ↔
// Audit keep `serial`; Clients ↔ the SSIDs keep `ssid`; `mac` is Clients'
// alone), and a page reads only the facets it understands — a `serial` pasted
// onto Design › Wireless is inert there and falls off at the next link. The
// primary navigation carries none (#702): its entries are places to be, and
// every facet drops when one is clicked, the scope alone staying. A page's
// own tabs are places too, and carry none either (#874). Names are looked
// up live by the context bar (`FacetBar.tsx`), never stored here.
//
// **The keys are addresses, not pages** (#874). A workspace page's tab is a
// segment of its address (`nav.ts`), so a page whose tabs understand
// different facets is several keys here: Operate › Wireless is Clients at its
// own address, and Design › Changes' All differences is `/design/changes/differences`.
// A tab absent here understands none.
//
// `tab` (#388) is the one facet that names no object: it is which section of
// a device's page is open — ports, links, neighbourhood, console, audit — so
// a link can open a device *on* its console and the back button returns to
// the section left. On Operate › Devices it is meaningful only beside
// `serial`. The context bar draws no chip for it: it is the page's state, not
// the page's subject. The plan's sections used it too (#519), until #874 made
// them tabs of Design › Physical and Design › Wired, which a segment names.
// On the comparison it is which facet of the read is open (#643), and
// `disposition` is the same kind of facet there: which of the five
// dispositions the table is narrowed to, a page state that belongs in the
// address so a link can open the comparison on its `differs` rows, and no chip
// either. `status` and `unit` (#694) are that kind again, on per-unit
// access: which of §11.4's four facets the table is narrowed to, and the unit
// name it is filtered to. Both are the list endpoint's own parameters, so the
// address says what the request asked for; neither draws a chip. `q` and `vlan`
// (#784) join them on the same page and for the same reason — `q` being the one
// that is a **search** rather than a filter (ADR-0109 decision 2), which changes
// what the server does with it and nothing about how the address carries it.
//
// `port` (#875) names one port of the switch `serial` names, and is meaningful
// only beside it: *Change this in Design* on a device's page lands on Design ›
// Wired's Ports tab with the switch chosen and that port pressed. Like `tab` it
// draws no chip — it is where the page opens, and the device's chip clears it.
//
// `link` (#883) names one declared link of the site by its id. Design ›
// Wired's Cabling list writes it for the row chosen there, and its *On the
// drawing* link carries it to the Topology tab, which lights that cable. It is
// page state like `port` and draws no chip: a link has no page of its own to
// be the subject of.
//
// `claim` and `claim_type` (#1483) open Design › Physical's Equipment with the
// claim panel open and its serial — and the type the cell reports, where it
// reported one — filled in. Operate's topology writes them for a neighbour the
// site's cell holds unclaimed: claiming is Design's (ADR-0149 decision 1), so
// Operate offers the one click to the panel rather than the panel. Page state
// like `port`, with no chip: the panel is the page's, not its subject.
//
// `subnet` (#1600) is Clients' filter box when what was typed there is an IPv4
// subnet: `subnet=10.30.1.0/24`, the network the person named with its host
// bits cleared. It is the prefix they typed and never one Zenith holds — no
// `/v1` field carries a subnet — so it is the table's own narrowing, like `q`
// and `vlan` on Access, with no chip: the box shows it, and emptying the box or
// Clear removes it. `useFacetDraft` is how a box and a facet agree.
//
// **Two facets may name more than one value** (#1602), on Clients alone: `ssid`
// and `unit`, which its pickers set, each value its own parameter —
// `?ssid=Office&ssid=Guest` — because an SSID may hold any character a
// separator could be. Every other facet, and every other page, reads the first
// (`parseFacets`), so the multi-valued pair is Clients' and changes nothing for a
// page that reads one. `useFacetValues` reads and writes the whole set; a write
// to another facet keeps it (`facetSearch`), and a link to another page carries
// a facet only while it names one value (`carryFacets`): a set of SSIDs is a
// narrowing of this list, not an SSID another page can open on.
//
// `unit` means two things, by page. On Operate › Access it is the unit name the
// list endpoint filters on (#694); on Clients it is a PAN's `id` (#1602), since
// a unit's name is not unique (migration `0041`). So it never travels between
// pages, like `tab`, and it goes with the site it was set at.
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type FacetName = "serial" | "network" | "mac" | "ssid" | "target_type" | "target_id" | "tab" | "disposition" | "status" | "unit" | "q" | "vlan" | "port" | "link" | "claim" | "claim_type" | "subnet";
export type Facets = Partial<Record<FacetName, string>>;

export const FACET_NAMES: readonly FacetName[] = ["serial", "network", "mac", "ssid", "target_type", "target_id", "tab", "disposition", "status", "unit", "q", "vlan", "port", "link", "claim", "claim_type", "subnet"];

/** Which addresses understand which facets. An address absent here has none. */
const PAGES: Record<string, readonly FacetName[]> = {
  "/operate/devices": ["serial", "tab"],
  // Clients, the first of Operate › Wireless's tabs.
  "/operate/wireless": ["serial", "mac", "ssid", "subnet", "unit"],
  // By VLAN, on the VLAN a Design › VLANs card links it to (ADR-0312 7a).
  "/operate/wireless/by-vlan": ["vlan"],
  // Radios, narrowed to the radios carrying one SSID: where SSIDs on air's
  // "+N more…" lands (#1280).
  "/operate/wireless/radios": ["ssid"],
  // Per-unit keys, the first of Operate › Access's tabs. `network` there is
  // the collection's `network_id` (#881): the units a network serves.
  "/operate/access": ["status", "unit", "q", "vlan", "network"],
  // Equipment, the site's claimed devices, narrowed to one (#875), and with
  // the claim panel opened for a serial (#1483).
  "/design/physical/equipment": ["serial", "claim", "claim_type"],
  // A switch's ports, chosen by its serial, with one port pressed (#875), and
  // which of its sub-tabs is open (#1891): `tab` is `switch` or `advanced`, and
  // absent for the Ports sub-tab.
  "/design/wired/ports": ["serial", "port", "tab"],
  // The topology, lighting one declared link, and the Cabling list with that
  // link's row chosen (#883).
  "/design/wired": ["link"],
  "/design/wired/cabling": ["link"],
  // The same two in Operate › Wired (#1354), where the drawing and the list
  // are tabs of the section that reads.
  "/operate/wired": ["link"],
  "/operate/wired/cabling": ["link"],
  // The site's networks, the first of Design › Wireless's tabs.
  "/design/wireless": ["network", "ssid"],
  "/design/changes/differences": ["tab", "disposition"],
  "/audit": ["serial", "target_type", "target_id"],
};

/** The facets that name something inside one site, and so do not survive the
 * scope picker moving to another one: the device is not there. An audit
 * target, a station's address and an SSID are strings a page filters on at
 * any site — a laptop roams, and a customer's sites often share an SSID — so
 * they stay, and a site where they match nothing says so. `tab` and `port` go
 * with `serial`: a section of a device's page, or a port of a switch, is
 * nothing without the device; and a
 * facet of a site's comparison is that site's. `unit` goes with them rather than
 * with the SSID it resembles: a unit name identifies a household in one
 * building, and carrying "Unit 146" to another property would name a
 * different resident. A `link` is one of the site's rows, and a `claim` is a
 * serial to claim at the site it was offered at. `status`, `q` and `vlan` are the table's own narrowings at
 * the site they were set on, so they leave with it — a VLAN in particular names
 * a different unit at every property, and a `subnet` a different network. */
const SITE_BOUND: readonly FacetName[] = ["serial", "network", "tab", "disposition", "status", "unit", "q", "vlan", "port", "link", "claim", "claim_type", "subnet"];

/** The kinds of object a page names and an operator navigates by (#263):
 * each has one facet and one page that owns it. A device is an access point
 * or a switch, named by serial; a station by its MAC in §8.2's spelling —
 * twelve lowercase hex digits, no separators; an SSID by the string itself. */
export type ObjectKind = "device" | "station" | "ssid";
export const OBJECT_HOME: Record<ObjectKind, { facet: FacetName; page: string }> = {
  device: { facet: "serial", page: "/operate/devices" },
  station: { facet: "mac", page: "/operate/wireless" },
  ssid: { facet: "ssid", page: "/design/wireless" },
};

/** Where a kind of object opens from a Design page, where that differs from
 * its home (#1888): a device named anywhere in Design opens Design ›
 * Physical › Equipment narrowed to it, whose drawer offers its Operate page
 * as a second link, so a designer following a device's name stays in the
 * workspace where the network is changed (ADR-0149). Operate keeps
 * `OBJECT_HOME`. A station and an SSID have one home whichever workspace
 * names them. */
const DESIGN_HOME: Partial<Record<ObjectKind, { facet: FacetName; page: string }>> = {
  device: { facet: "serial", page: "/design/physical/equipment" },
};

/** The page an object named on `pathname` opens: its Design home on a page
 * of Design, its home everywhere else. The one place a link's destination is
 * decided (`ObjectLink.tsx`). */
export function homeOf(kind: ObjectKind, pathname: string): { facet: FacetName; page: string } {
  const design = pathname === "/design" || pathname.startsWith("/design/");
  return (design && DESIGN_HOME[kind]) || OBJECT_HOME[kind];
}

/** The facets a page understands; none for a page not listed. */
export function facetsOf(pathname: string): readonly FacetName[] {
  return PAGES[pathname] ?? [];
}

/** The facets an address carries — those `pathname`'s page understands when
 * one is given, every one otherwise. */
export function parseFacets(search: string, pathname?: string): Facets {
  const p = new URLSearchParams(search);
  const out: Facets = {};
  for (const name of pathname === undefined ? FACET_NAMES : facetsOf(pathname)) {
    const v = p.get(name);
    if (v) out[name] = v;
  }
  return out;
}

/** Every value the address gives `name`, in order, without repeats or blanks
 * (#1602): one for a facet set once, none for one not set. */
export function facetValues(search: string, name: FacetName): string[] {
  return [...new Set(new URLSearchParams(search).getAll(name).filter((v) => v !== ""))];
}

/** `search` with every facet parameter replaced by `facets`'; every other
 * parameter — the scope among them — is kept. A facet absent from `facets`
 * is removed, so a page's `set` also sheds a facet it does not understand. */
export function facetSearch(facets: Facets, search = ""): string {
  const p = new URLSearchParams(search);
  for (const name of FACET_NAMES) {
    const v = facets[name];
    // Left as the address has it — its first value, which is what
    // `parseFacets` read — a facet keeps every value it had (#1602), so
    // narrowing another facet does not drop the second SSID picked.
    if (v && p.get(name) === v) continue;
    if (v) p.set(name, v); else p.delete(name);
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

/** The facets a link from `from` carries to the page at `to`: those the page
 * being left understands and the page being reached understands as well.
 * `tab` is the one that never travels: it is a page's own section, and two
 * pages that both have sections do not share their names (#519) — a device
 * open on its console says nothing about which of the comparison's facets to open.
 * `unit` never travels either (#1602): it names a unit by name on Access and by
 * a PAN's id on Clients. */
export function carryFacets(from: { pathname: string; search: string }, to: string): Facets {
  const here = parseFacets(from.search, from.pathname);
  const out: Facets = {};
  for (const name of facetsOf(to)) {
    if ((name === "tab" || name === "unit") && to !== from.pathname) continue;
    // A set of values is the page's own narrowing (#1602): carrying its first
    // would open the next page on one of them as though it were the choice.
    if (facetValues(from.search, name).length > 1) continue;
    const v = here[name];
    if (v) out[name] = v;
  }
  return out;
}

/** `search` without the site-bound facets, for a scope change that leaves the
 * site they named. */
export function leaveSite(search: string): string {
  const p = new URLSearchParams(search);
  for (const name of SITE_BOUND) p.delete(name);
  const s = p.toString();
  return s ? "?" + s : "";
}

/** The facets the address carries, and a way to change them: `set` merges a
 * patch (a facet set to `undefined` is removed) and writes the address;
 * `clear` removes one. Every facet is parsed, whatever the page: a page reads
 * the ones it understands and the rest are inert, and the page itself — not
 * the address it happens to be rendered at — is the authority on which. */
export function useFacets(): { facets: Facets; set(patch: Facets, opts?: { replace?: boolean }): void; clear(name: FacetName): void } {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;
  const facets = useMemo(() => parseFacets(search), [search]);
  const set = useCallback((patch: Facets, opts?: { replace?: boolean }) => {
    navigate({ pathname, search: facetSearch({ ...facets, ...patch }, search) }, opts);
  }, [navigate, pathname, search, facets]);
  const clear = useCallback((name: FacetName) => set({ [name]: undefined }), [set]);
  return { facets, set, clear };
}

/** Every value of a facet that may name several (#1602), and a way to replace
 * the set: each value is its own parameter, an empty set removes the facet,
 * and every other parameter is kept. Written as a push, like `set`: a pick is
 * a place the back button returns from. */
export function useFacetValues(name: FacetName): { values: string[]; set(values: string[]): void } {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const values = useMemo(() => facetValues(search, name), [search, name]);
  const set = useCallback((next: string[]) => {
    const p = new URLSearchParams(search);
    p.delete(name);
    for (const v of new Set(next)) if (v !== "") p.append(name, v);
    const s = p.toString();
    navigate({ pathname, search: s ? "?" + s : "" });
  }, [navigate, pathname, search, name]);
  return { values, set };
}

/** A text box whose accepted value is also a facet (#1600): what the person
 * types stays theirs, as typed, while the facet carries `canonical(text)` — or
 * nothing, when the text is not a value the facet holds. So a half-typed value
 * is never rewritten under the cursor, a link and a reload carry what was
 * applied, and a facet that changes from outside — the back button, a link —
 * replaces the text with itself.
 *
 * `key` is the list the text applies to: when it changes the text starts again
 * from the facet, as a page's other narrowing does. The facet is written as a
 * replace, because each keystroke is not a place to go back to. */
export function useFacetDraft(name: FacetName, key: string, canonical: (text: string) => string | undefined): { text: string; setText(text: string): void; reset(): void } {
  const { facets, set } = useFacets();
  const held = facets[name] ?? "";
  const [stored, setStored] = useState({ key, text: held, synced: held });
  const draft = stored.key === key && stored.synced === held ? stored : { key, text: held, synced: held };
  const write = (text: string, value: string) => {
    setStored({ key, text, synced: value });
    if (value !== held) set({ [name]: value || undefined }, { replace: true });
  };
  return {
    text: draft.text,
    setText: (text) => write(text, canonical(text) ?? ""),
    reset: () => write("", ""),
  };
}
